import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * GuestUpgradeHint — site-wide top banner for anonymous users with
 * accumulated usage. Fires when the user is signed in as a guest AND
 * any of the three trigger conditions hits:
 *
 *   - 3 or more lists created
 *   - 1 or more reminders set
 *   - 7 or more days since first sign-in
 *
 * Walter 2026-05-28 — the guest path is the friction-free entry point
 * (no email, no password). It's good for the funnel but creates a
 * silent risk: anonymous data lives only in the browser's IndexedDB,
 * so a cleared profile / lost device = lost work. Additionally
 * reminders set under a guest account silently never fire (no email
 * or push channel). The banner makes the upgrade prompt proactive
 * without being immediate — usage-based, not time-on-page-based.
 *
 * Dismissal:
 *   - "remind me later" → 3-day snooze via localStorage timestamp, but
 *     bypassed the moment a new reminder is set (see readDismissalState).
 *
 * Walter 2026-06-15: dropped the permanent "don't remind me" option.
 * A guest's reminders silently don't fire, so the warning should
 * re-surface every time they set another reminder — there's no
 * "leave me alone forever" that's safe for the user here. Snooze is
 * the only dismissal, and even that lifts on the next reminder.
 *
 * Why localStorage instead of userPrefs Firestore? Anonymous sessions
 * still have a Firestore prefs doc, but the user can clear IndexedDB
 * and start fresh — which is exactly the risk we're warning about.
 * Using localStorage means the dismissal state is also browser-local;
 * if they clear storage and start over, they'll see the banner again
 * with the new account. That's the right behaviour.
 *
 * Suppressed on marketing routes (no app state to lose there).
 */
const SNOOZE_UNTIL_KEY = 'lists_guest_upgrade_snoozed_until';
const SNOOZE_LISTS_KEY = 'lists_guest_upgrade_snoozed_at_lists';
const SNOOZE_REMINDERS_KEY = 'lists_guest_upgrade_snoozed_at_reminders';
// Walter 2026-05-28: once the user has dismissed the 7-day time-based
// nudge (either snooze or permanent), the time trigger is done — the
// user has clearly said "I'm fine, leave me alone about the duration."
// Boards / reminders escalation still works (those signal real
// accumulating stakes), but the time trigger doesn't re-fire.
const TIME_TRIGGER_ACK_KEY = 'lists_guest_upgrade_time_trigger_acked';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

// Trigger thresholds. Tuned for Walter's "loss aversion kicks in
// fastest once you've built something or scheduled something" goal.
// Walter 2026-05-28: days threshold bumped from 2 → 7. Earlier is too
// pushy; by a week, the user has had time to evaluate and trust the
// app, and the upgrade nudge reads more like "you're sticking with
// it, lock it in" than "we noticed you're here."
const LIST_TRIGGER = 3;
const REMINDER_TRIGGER = 1;
const DAYS_TRIGGER = 7;

const dismissButtonStyle = {
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 4,
  padding: '4px 10px',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(10px * var(--ui-scale, 1))',
  whiteSpace: 'nowrap',
};

const signInButtonStyle = {
  ...dismissButtonStyle,
  background: 'rgba(255,255,255,0.18)',
  fontWeight: 600,
};

// Walter 2026-05-28: snooze IS time-based (3 days) but ALSO bypassed
// when the user crosses a new threshold since dismissal. If they had
// 1 reminder when they snoozed and now have 2, the loss-aversion
// signal is stronger and worth re-firing. Same for boards.
// Walter 2026-06-15: there is no permanent dismissal anymore — snooze
// is the only state, and it lifts on the next reminder.
function readDismissalState({ listsCount, remindersCount }) {
  try {
    const until = parseInt(localStorage.getItem(SNOOZE_UNTIL_KEY) || '0', 10);
    if (!until || Date.now() >= until) return 'active';
    const snoozedAtLists = parseInt(localStorage.getItem(SNOOZE_LISTS_KEY) || '0', 10);
    const snoozedAtReminders = parseInt(localStorage.getItem(SNOOZE_REMINDERS_KEY) || '0', 10);
    if (listsCount > snoozedAtLists || remindersCount > snoozedAtReminders) {
      // User crossed into new territory — un-snooze.
      // Walter 2026-05-28: drop ONLY the time key. Keeping the snoozed-
      // at counts is what lets the headline selection downstream know
      // WHICH trigger crossed (lists vs reminders) — without them, the
      // banner returns with the priority-order copy instead of the
      // copy that matches what actually changed.
      try { localStorage.removeItem(SNOOZE_UNTIL_KEY); } catch { /* ignore */ }
      return 'active';
    }
    return 'snoozed';
  } catch {
    return 'active';
  }
}

export default function GuestUpgradeHint({
  user,
  listsCount = 0,
  remindersCount = 0,
  onSignIn,
}) {
  const [dismissalState, setDismissalState] = useState(() =>
    readDismissalState({ listsCount, remindersCount })
  );
  const location = useLocation();

  // Re-check dismissal on focus AND whenever the trigger counts change.
  // The count-change check is what lets the banner re-fire mid-session
  // when the user adds a 2nd reminder after dismissing on the 1st.
  useEffect(() => {
    setDismissalState(readDismissalState({ listsCount, remindersCount }));
  }, [listsCount, remindersCount]);
  useEffect(() => {
    const onFocus = () => setDismissalState(readDismissalState({ listsCount, remindersCount }));
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [listsCount, remindersCount]);

  // Suppress on marketing routes — same rationale as ExtensionBlockHint.
  const onMarketingRoute = /^\/(landing|about|help|privacy|terms|contact)\b/.test(
    location.pathname,
  ) || location.pathname === '/';

  // Not a guest? Nothing to show.
  if (!user?.isAnonymous) return null;
  if (dismissalState !== 'active' || onMarketingRoute) return null;

  // Trigger conditions.
  const accountAgeMs = user.metadata?.creationTime
    ? Date.now() - new Date(user.metadata.creationTime).getTime()
    : 0;
  const accountAgeDays = accountAgeMs / (24 * 60 * 60 * 1000);
  let timeTriggerAcked = false;
  try { timeTriggerAcked = localStorage.getItem(TIME_TRIGGER_ACK_KEY) === '1'; } catch { /* ignore */ }
  const timeTriggered = accountAgeDays >= DAYS_TRIGGER && !timeTriggerAcked;
  // Walter 2026-05-31: PULLED BACK to the single strongest, most-
  // actionable case — a guest who has set a reminder that silently
  // won't fire. The sign-in / install / boards nudging moved to the
  // calm sidebar OnboardingChecklist, so this top banner no longer
  // double-nags. The lists/time triggers (and their accAge/timeAck
  // machinery above) are kept dormant — flip them back here if we
  // ever want the banner to carry more than the reminders warning.
  const triggered = remindersCount >= REMINDER_TRIGGER;
  // Quiet the now-unused locals so lint/build stays clean while the
  // dormant triggers remain documented above.
  void listsCount; void timeTriggered;
  if (!triggered) return null;

  const snooze = () => {
    // Walter 2026-05-28: store the trigger values AT TIME OF SNOOZE so
    // readDismissalState can detect when the user crosses a new
    // threshold and un-snooze. "remind me later" doesn't suppress
    // forever — it just hides until either 3 days pass OR the
    // situation changes (more lists, more reminders).
    //
    // Also: ack the time trigger if it was active. Walter's call —
    // once the user has dismissed the 7-day "you've been with us a
    // week" prompt, don't keep asking for time reasons; only
    // escalating boards/reminders signals warrant a return.
    try {
      localStorage.setItem(SNOOZE_UNTIL_KEY, String(Date.now() + SNOOZE_MS));
      localStorage.setItem(SNOOZE_LISTS_KEY, String(listsCount));
      localStorage.setItem(SNOOZE_REMINDERS_KEY, String(remindersCount));
      if (timeTriggered) localStorage.setItem(TIME_TRIGGER_ACK_KEY, '1');
    } catch { /* ignore */ }
    setDismissalState('snoozed');
  };

  // Walter 2026-05-28: pick the headline that matches what most
  // recently brought the banner back. Two cases:
  //
  //  (1) Returning after a snooze — the most informative copy is
  //      whichever trigger CROSSED since the snooze (e.g. boards went
  //      2 → 3, reminders unchanged → boards copy wins). Read the
  //      snoozed-at-* values from localStorage to detect.
  //
  //  (2) First-time appearance / no snooze data — fall back to the
  //      priority order: reminders > boards > time. Reminders is the
  //      most actionable failure mode (a silent reminder is worse than
  //      a potentially-lost list, since the user is actively relying
  //      on it to ping them).
  let snoozedAtLists = -1;
  let snoozedAtReminders = -1;
  try {
    const sl = localStorage.getItem(SNOOZE_LISTS_KEY);
    const sr = localStorage.getItem(SNOOZE_REMINDERS_KEY);
    if (sl != null) snoozedAtLists = parseInt(sl, 10);
    if (sr != null) snoozedAtReminders = parseInt(sr, 10);
  } catch { /* ignore */ }
  const remindersCrossed = snoozedAtReminders >= 0 && remindersCount > snoozedAtReminders;
  const listsCrossed = snoozedAtLists >= 0 && listsCount > snoozedAtLists;

  let headline;
  if (remindersCrossed) {
    headline = "your reminders aren't actually firing — sign in to enable them.";
  } else if (listsCrossed) {
    headline = "your lists live only on this device — sign in to save them and access them anywhere.";
  } else if (remindersCount >= REMINDER_TRIGGER) {
    headline = "your reminders aren't actually firing — sign in to enable them.";
  } else if (listsCount >= LIST_TRIGGER) {
    headline = "your lists live only on this device — sign in to save them and access them anywhere.";
  } else {
    // 7-day time-only trigger — leans on the user's accumulated trust
    // in the app rather than loss-aversion, since they haven't built
    // much or set reminders yet by this point.
    headline = "a week in as a guest — if listicity's working for you, sign in to keep your work safe across devices.";
  }

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        // Walter's QA pass 2026-08-25: sit BELOW the header, not over it. At
        // top:0 the card is full-width on a phone and covers the whole header
        // row — measured on a 375px viewport, elementFromPoint over the
        // settings button returned the banner, so settings / search / new
        // list / board menu were all unreachable until the guest dismissed
        // it. The wrapper is pointer-events:none, but the card itself isn't,
        // and on mobile the card IS the header's width.
        top: 'var(--header-h, 48px)',
        left: 0,
        right: 0,
        zIndex: 1750,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '10px',
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: '100%',
          background: 'rgba(232, 98, 44, 0.96)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          lineHeight: 1.45,
          pointerEvents: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1.5 }}>👤</span>
          <span style={{ flex: 1 }}>{headline}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          {onSignIn && (
            <button
              type="button"
              onClick={onSignIn}
              style={signInButtonStyle}
              title="open the sign-in panel"
            >
              sign in
            </button>
          )}
          <button
            type="button"
            onClick={snooze}
            style={dismissButtonStyle}
            title="hide for 3 days (returns when you set another reminder)"
          >
            remind me later
          </button>
        </div>
      </div>
    </div>
  );
}
